import { ApiResponse, Profile } from "@readyfit-common/models";
import { Permission } from "src/grading/utils/permissionsHelpers";
import { apiSlice } from "../../apiSlice";

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

export interface ForgotPasswordPayload {
  email: string;
  code?: string;
  password?: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: "/api/account/login",
        method: "POST",
        body: { ...credentials },
      }),
      transformResponse: (response: { data: LoginResponse }) => response.data,
    }),
    resetPassword: builder.mutation<
      ForgotPasswordResponse,
      ForgotPasswordPayload
    >({
      query: (credentials) => ({
        url: "/api/account/forgot-password",
        method: "PUT",
        body: { ...credentials },
      }),
    }),
    getUser: builder.query<any, void>({
      query: () => ({
        url: "/api/account/me",
        method: "GET",
      }),
      transformResponse: (response: { data: { profile: Profile } }) =>
        response.data,
      providesTags: ["Profile"],
    }),
    getPermissions: builder.query<any, void>({
      query: () => ({
        url: "/api/account/me/permissions",
        method: "GET",
      }),
      transformResponse: (response: { data: { permissions: Permission[] } }) =>
        response.data,
    }),
    updateProfile: builder.mutation<
      ApiResponse<void>,
      Partial<
        Pick<
          Profile,
          "firstName" | "lastName" | "coachInfo" | "defaultMeasurementUnit"
        >
      >
    >({
      query: (profile) => ({
        url: "/api/account/me",
        method: "PUT",
        body: { ...profile },
      }),
      invalidatesTags: ["Profile"],
    }),
    updateProfileImage: builder.mutation<ApiResponse<void>, FormData>({
      query: (body) => {
        return {
          url: "/api/account/me/profile-image",
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["Profile"],
    }),
    updatePassword: builder.mutation<
      ApiResponse<void>,
      { previousPassword: string; proposedPassword: string }
    >({
      query: (payload) => ({
        url: "/api/account/me/password",
        method: "PUT",
        body: { ...payload },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useResetPasswordMutation,
  useLazyGetPermissionsQuery,
  useUpdateProfileMutation,
  useUpdateProfileImageMutation,
  useUpdatePasswordMutation,
} = authApiSlice;
